import { createSelector, createSlice } from '@reduxjs/toolkit';
import {
	filterTrafficData,
	createGeoJsonSource,
	getAMAltitudeDisplayText,
	getAMGroundSpeedDisplayText,
	createLineAlongHeading,
	convertDegreesToThreeDigit
} from 'components/airSpace/airspaceUtils';
import { initialLiveTrafficState } from 'reducers/initialStates';
import circle from '@turf/circle';
import destination from '@turf/destination';
import bearing from '@turf/bearing';
import distance from '@turf/distance';
import midpoint from '@turf/midpoint';
import { unitsSelector } from 'reducers/globalSlice';
import { convert } from 'services/unitConversionService';

const liveTrafficSlice = createSlice({
	name: 'liveTraffic',
	initialState: {
		//Set by configureStore
		reconnectAttempts: 0,
		nextRetryTime: null
	},
	reducers: {
		setAMTrafficFeatures(state, action) {
			//This is filtered out in dev tools so that is doesn't clutter the feed (see configureStore.js)
			state.airMeshFeatures = action.payload;
			// Check if any of the features are simulated
			state.hasSimulatedTraffic = action.payload.some(feature => feature.properties?.is_sim);
		},
		setAMWebSocketConnected(state, action) {
			state.airMeshWebsocketConnected = action.payload;
		},
		setAMConnectionError(state, action) {
			state.airMeshConnectionError = action.payload;
		},
		setAMConnectionLoading(state, action) {
			state.airMeshConnectionLoading = action.payload;
		},
		setAMPusherInstance(state, action) {
			state.airMeshPusherInstance = action.payload;
		},
		setPauseTrafficUpdates(state, action) {
			state.pauseTrafficUpdates = action.payload;
		},
		setAirMeshAircraftTypes(state, action) {
			state.airMeshAircraftTypes = action.payload;
		},
		setAirMeshProviderTypes(state, action) {
			state.airMeshProviderTypes = action.payload;
		},
		setAirMeshSignalTypes(state, action) {
			state.airMeshSignalTypes = action.payload;
		},
		setAirMeshSignalProviderTypes(state, action) {
			state.airMeshSignalProviderTypes = action.payload;
		},
		// Batched action to set all filter types at once (performance optimization)
		setAllFilterTypes(state, action) {
			const { aircraftTypes, providerTypes, signalTypes, signalProviderTypes } = action.payload;
			if (aircraftTypes) state.airMeshAircraftTypes = aircraftTypes;
			if (providerTypes) state.airMeshProviderTypes = providerTypes;
			if (signalTypes) state.airMeshSignalTypes = signalTypes;
			if (signalProviderTypes) state.airMeshSignalProviderTypes = signalProviderTypes;
		},
		setAircraftTypesFilterValue(state, action) {
			state.aircraftTypesFilterValue = action.payload;
		},
		setProviderTypesFilterValue(state, action) {
			state.providerTypesFilterValue = action.payload;
		},
		setOwnshipID(state, action) {
			state.ownshipID = action.payload;
		},
		setShowAircraftInfoList(state, action) {
			state.showAircraftInfoList = action.payload;
		},
		setShowBearingRangeList(state, action) {
			state.showBearingRangeList = action.payload;
		},
		handleAircraftClick(state, action) {
			const { aircraftId, ownshipID } = action.payload;

			// Don't process ownship clicks
			if (ownshipID === aircraftId) {
				return;
			}

			// UAV/aircraft clicks are handled in the map component (pinning as ownships)
			// This action only processes other aircraft types

			// Check if there is an ownship to determine behavior
			const hasOwnship = ownshipID !== null;

			// Check if item is in showBearingRangeList first (priority removal)
			if (state.showBearingRangeList.includes(aircraftId)) {
				// Remove from both lists (back to live traffic list)
				state.showAircraftInfoList = state.showAircraftInfoList.filter(id => id !== aircraftId);
				state.showBearingRangeList = state.showBearingRangeList.filter(id => id !== aircraftId);
			} else if (state.showAircraftInfoList.includes(aircraftId)) {
				if (hasOwnship) {
					// Add to showBearingRangeList while keeping in showAircraftInfoList
					state.showBearingRangeList = [...state.showBearingRangeList, aircraftId];
				} else {
					// Remove from showAircraftInfoList if no ownship
					state.showAircraftInfoList = state.showAircraftInfoList.filter(id => id !== aircraftId);
				}
			} else {
				// Add to showAircraftInfoList
				state.showAircraftInfoList = [...state.showAircraftInfoList, aircraftId];
			}
		},
		setAirMeshChannels(state, action) {
			state.airMeshChannels = action.payload;
		},
		setSignalTypesFilterValue(state, action) {
			state.signalTypesFilterValue = action.payload;
		},
		setSignalProvidersFilterValue(state, action) {
			state.signalProvidersFilterValue = action.payload;
		},
		setHideSimulatedTraffic(state, action) {
			state.hideSimulatedTraffic = action.payload;
		},
		resetAMState(state) {
			state.airMeshFeatures = [];
			state.airMeshWebsocketConnected = false;
			state.airMeshConnectionError = false;
			state.airMeshConnectionLoading = false;
			state.airMeshPusherInstance = null;
		},
		resetLiveTrafficSlice() {
			return initialLiveTrafficState;
		},
		setReconnectAttempts(state, action) {
			state.reconnectAttempts = action.payload;
		},
		setNextRetryTime(state, action) {
			state.nextRetryTime = action.payload;
		},
		setOwnshipRing1(state, action) {
			state.ownshipRing1 = {
				...state.ownshipRing1,
				...action.payload
			};
		},
		setOwnshipRing2(state, action) {
			state.ownshipRing2 = {
				...state.ownshipRing2,
				...action.payload
			};
		},
		setOwnshipRing3(state, action) {
			state.ownshipRing3 = {
				...state.ownshipRing3,
				...action.payload
			};
		},
		setLiveTrafficMetricSpeedUnit(state, action) {
			state.liveTrafficMetricSpeedUnit = action.payload;
		},
		setLiveTrafficImperialSpeedUnit(state, action) {
			state.liveTrafficImperialSpeedUnit = action.payload;
		}
	}
});

export const {
	setAMTrafficFeatures,
	setAMWebSocketConnected,
	setAMConnectionError,
	setAMConnectionLoading,
	setAMPusherInstance,
	resetAMState,
	setPauseTrafficUpdates,
	setAirMeshAircraftTypes,
	setAirMeshProviderTypes,
	setAircraftTypesFilterValue,
	setProviderTypesFilterValue,
	setOwnshipID,
	setShowAircraftInfoList,
	setShowBearingRangeList,
	setAirMeshChannels,
	resetLiveTrafficSlice,
	setSignalTypesFilterValue,
	setSignalProvidersFilterValue,
	setHideSimulatedTraffic,
	setAirMeshSignalTypes,
	setAirMeshSignalProviderTypes,
	setAllFilterTypes,
	setReconnectAttempts,
	setNextRetryTime,
	handleAircraftClick,
	setOwnshipRing1,
	setOwnshipRing2,
	setOwnshipRing3,
	setLiveTrafficMetricSpeedUnit,
	setLiveTrafficImperialSpeedUnit
} = liveTrafficSlice.actions;

export default liveTrafficSlice.reducer;

export const liveTrafficActions = Object.values(liveTrafficSlice.actions).map(
	action => action.type
);

export const airMeshFeaturesSelector = state => state.liveTraffic.airMeshFeatures;
export const ownshipIDSelector = state => state.liveTraffic.ownshipID;

export const aircraftTypesFilterValueSelector = state => state.liveTraffic.aircraftTypesFilterValue;
export const providerTypesFilterValueSelector = state => state.liveTraffic.providerTypesFilterValue;
export const signalTypesFilterValueSelector = state => state.liveTraffic.signalTypesFilterValue;
export const signalProvidersFilterValueSelector = state =>
	state.liveTraffic.signalProvidersFilterValue;

// Full traffic features with all properties - used by table and other selectors
// NOTE: Map bounds filtering now happens at data ingestion level (airMeshHelpers.js)
// so features in Redux state are already filtered to the current viewport
export const trafficFeaturesSelector = createSelector(
	[
		airMeshFeaturesSelector,
		aircraftTypesFilterValueSelector,
		providerTypesFilterValueSelector,
		signalTypesFilterValueSelector,
		signalProvidersFilterValueSelector,
		state => state.liveTraffic.hideSimulatedTraffic
	],
	(
		airMeshFeatures,
		aircraftTypesFilterValue,
		providerTypesFilterValue,
		signalTypesFilterValue,
		signalProvidersFilterValue,
		hideSimulatedTraffic
	) => {
		let filtered = filterTrafficData(
			airMeshFeatures,
			aircraftTypesFilterValue,
			providerTypesFilterValue,
			signalTypesFilterValue,
			signalProvidersFilterValue
		);

		if (hideSimulatedTraffic) {
			filtered = filtered.filter(feature => !feature.properties?.is_sim);
		}

		return filtered;
	}
);

// Optimized traffic features for map rendering - only includes properties used by map layers
export const airMeshWebsocketConnectedSelector = state =>
	state.liveTraffic.airMeshWebsocketConnected;
export const airMeshConnectionErrorSelector = state => state.liveTraffic.airMeshConnectionError;
export const airMeshConnectionLoadingSelector = state => state.liveTraffic.airMeshConnectionLoading;
export const airMeshPusherInstanceSelector = state => state.liveTraffic.airMeshPusherInstance;
export const pauseTrafficUpdatesSelector = state => state.liveTraffic.pauseTrafficUpdates;
export const airMeshAircraftTypesSelector = state => state.liveTraffic.airMeshAircraftTypes;
export const airMeshProviderTypesSelector = state => state.liveTraffic.airMeshProviderTypes;
export const airMeshSignalTypesSelector = state => state.liveTraffic.airMeshSignalTypes;
export const airMeshSignalProviderTypesSelector = state =>
	state.liveTraffic.airMeshSignalProviderTypes;
export const showAircraftInfoListSelector = state => state.liveTraffic.showAircraftInfoList;
export const showBearingRangeListSelector = state => state.liveTraffic.showBearingRangeList;
export const airMeshChannelsSelector = state => state.liveTraffic.airMeshChannels;

export const hideSimulatedTrafficSelector = state => state.liveTraffic.hideSimulatedTraffic;
export const hasSimulatedTrafficSelector = state => state.liveTraffic.hasSimulatedTraffic;

export const reconnectAttemptsSelector = state => state.liveTraffic.reconnectAttempts;

export const nextRetryTimeSelector = state => state.liveTraffic.nextRetryTime;

// Derived selectors for ownship features and related sources
export const ownshipFeaturesSelector = createSelector(
	[trafficFeaturesSelector, ownshipIDSelector],
	(trafficFeatures, ownshipID) => {
		if (!ownshipID) return [];
		return (trafficFeatures || []).filter(f => f.id === ownshipID);
	}
);

export const ownshipRing1Selector = state => state.liveTraffic.ownshipRing1;
export const ownshipRing2Selector = state => state.liveTraffic.ownshipRing2;
export const ownshipRing3Selector = state => state.liveTraffic.ownshipRing3;

export const liveTrafficMetricSpeedUnitSelector = state =>
	state.liveTraffic.liveTrafficMetricSpeedUnit;
export const liveTrafficImperialSpeedUnitSelector = state =>
	state.liveTraffic.liveTrafficImperialSpeedUnit;

export const liveTrafficSelectedSpeedUnitSelector = createSelector(
	unitsSelector,
	liveTrafficMetricSpeedUnitSelector,
	liveTrafficImperialSpeedUnitSelector,
	(units, metricSpeedUnit, imperialSpeedUnit) => {
		const selectedSpeedUnit = units === 'metric' ? metricSpeedUnit : imperialSpeedUnit;
		return selectedSpeedUnit;
	}
);

export const ownshipSourceSelector = createSelector(
	[ownshipFeaturesSelector, ownshipRing1Selector, ownshipRing2Selector, ownshipRing3Selector],
	(ownshipFeatures, ownshipRing1, ownshipRing2, ownshipRing3) => {
		if (!ownshipFeatures.length) {
			return createGeoJsonSource([]);
		}

		const ring1Distance = convert(ownshipRing1.distanceNauticalMiles, 'nMi', 'mi', {
			ignoreUserSelectedUnitsSystem: true
		});
		const ring2Distance = convert(ownshipRing2.distanceNauticalMiles, 'nMi', 'mi', {
			ignoreUserSelectedUnitsSystem: true
		});
		const ring3Distance = convert(ownshipRing3.distanceNauticalMiles, 'nMi', 'mi', {
			ignoreUserSelectedUnitsSystem: true
		});

		const allFeatures = [];
		ownshipFeatures.forEach(feature => {
			const { geometry, properties } = feature;
			const { coordinates } = geometry;
			const { heading } = properties;

			// Add the original ownship point feature
			allFeatures.push({
				...feature,
				properties: {
					...properties,
					type: 'ownship-point'
				}
			});

			const ring1CircleFeature = circle(coordinates, ring1Distance, { units: 'miles' });
			allFeatures.push({
				...ring1CircleFeature,
				properties: {
					...properties,
					type: 'ownship-ring-1'
				}
			});
			const ring1TextPoint = destination(coordinates, ring1Distance, 0, { units: 'miles' });
			allFeatures.push({
				...ring1TextPoint,
				properties: {
					...properties,
					type: 'ownship-ring-1-text',
					label: ownshipRing1.distanceNauticalMiles
				}
			});

			const ring2CircleFeature = circle(coordinates, ring2Distance, { units: 'miles' });
			allFeatures.push({
				...ring2CircleFeature,
				properties: {
					...properties,
					type: 'ownship-ring-2'
				}
			});

			const ring2TextPoint = destination(coordinates, ring2Distance, 0, { units: 'miles' });
			allFeatures.push({
				...ring2TextPoint,
				properties: {
					...properties,
					type: 'ownship-ring-2-text',
					label: ownshipRing2.distanceNauticalMiles
				}
			});

			const ring3CircleFeature = circle(coordinates, ring3Distance, { units: 'miles' });
			allFeatures.push({
				...ring3CircleFeature,
				properties: {
					...properties,
					type: 'ownship-ring-3'
				}
			});

			const ring3TextPoint = destination(coordinates, ring3Distance, 0, { units: 'miles' });
			allFeatures.push({
				...ring3TextPoint,
				properties: {
					...properties,
					type: 'ownship-ring-3-text',
					label: ownshipRing3.distanceNauticalMiles
				}
			});

			const line = createLineAlongHeading(coordinates, heading, ring1Distance);

			allFeatures.push({
				...line,
				properties: {
					...properties,
					type: 'ownship-ring-1'
				}
			});

			// Add text point feature (x mile along heading for text display)
			const textPoint = destination(coordinates, ring1Distance / 2, heading, { units: 'miles' });
			allFeatures.push({
				...textPoint,
				properties: {
					...properties,
					type: 'ownship-text',
					heading: convertDegreesToThreeDigit(heading) + '°'
				}
			});
		});

		return createGeoJsonSource(allFeatures);
	}
);

export const aircraftInfoSourceSelector = createSelector(
	[trafficFeaturesSelector, showAircraftInfoListSelector, liveTrafficSelectedSpeedUnitSelector],
	(trafficFeatures, showAircraftInfoList, liveTrafficSelectedSpeedUnit) => {
		// OPTIMIZATION: Early return if no aircraft selected (99% of the time)
		// Avoids filtering 1200 features and creating empty GeoJSON source
		if (!showAircraftInfoList || showAircraftInfoList.length === 0) {
			return createGeoJsonSource([]);
		}

		// Only process selected aircraft (typically 1-5 features)
		// This minimal spreading is acceptable for the small number of selected aircraft
		const features = (trafficFeatures || [])
			.filter(f => showAircraftInfoList.includes(f.properties.id))
			.map(feature => ({
				...feature,
				properties: {
					...feature.properties,
					speed_display: getAMGroundSpeedDisplayText({
						row: feature,
						selectedSpeedUnit: liveTrafficSelectedSpeedUnit
					}),
					altitude_display: getAMAltitudeDisplayText(
						feature.properties,
						feature.properties.altitude_agl_ft
					)
				}
			}));

		return createGeoJsonSource(features);
	}
);

export const bearingRangeLinesSourceSelector = createSelector(
	[ownshipFeaturesSelector, trafficFeaturesSelector, showBearingRangeListSelector, unitsSelector],
	(ownshipFeatures, trafficFeatures, showBearingRangeList, units) => {
		// OPTIMIZATION: Early return if no ownship or no bearing range lines selected
		if (!ownshipFeatures.length || !showBearingRangeList || showBearingRangeList.length === 0) {
			return createGeoJsonSource([]);
		}

		const ownshipCoords = ownshipFeatures[0].geometry.coordinates;
		const features = [];

		showBearingRangeList.forEach(targetId => {
			const targetFeature = trafficFeatures.find(f => f.id === targetId);
			if (!targetFeature) return;

			const targetCoords = targetFeature.geometry.coordinates;
			const roundedBearing = convertDegreesToThreeDigit(
				Math.round(bearing(ownshipCoords, targetCoords))
			);
			const roundedDistance = Math.round(
				distance(ownshipCoords, targetCoords, {
					units: units === 'imperial' ? 'miles' : 'kilometers'
				})
			);
			const lineMidpointFeature = midpoint(ownshipCoords, targetCoords);

			// Create line feature
			features.push({
				type: 'Feature',
				geometry: {
					type: 'LineString',
					coordinates: [ownshipCoords, targetCoords]
				},
				properties: {
					id: `bearing-line-${targetId}`,
					type: 'bearing-line'
				}
			});

			// Create text label feature at midpoint
			features.push({
				type: 'Feature',
				geometry: {
					type: 'Point',
					coordinates: lineMidpointFeature.geometry.coordinates
				},
				properties: {
					id: `bearing-text-${targetId}`,
					type: 'bearing-text',
					bearing: roundedBearing,
					distance: roundedDistance,
					text_field: `${roundedBearing}°\n${roundedDistance} ${units === 'imperial' ? 'mi' : 'km'}`
				}
			});
		});

		return createGeoJsonSource(features);
	}
);

// Selector that returns raw features for view and cursor layers (airspace view)
export const selectedAircraftsViewAndCursorFeaturesSelector = createSelector(
	[ownshipFeaturesSelector, trafficFeaturesSelector, showAircraftInfoListSelector],
	(ownshipFeatures, trafficFeatures, showAircraftInfoList) => {
		const aircraftFeatures = trafficFeatures.filter(f => showAircraftInfoList.includes(f.id));
		return [...ownshipFeatures, ...aircraftFeatures];
	}
);
